<script setup>
import { ref, computed, onMounted  } from 'vue';
const props = defineProps({
    web_site: Object,
});

const showFullText = ref(false)
const shouldShowToggle = ref(false)
const aboutText = ref(null)
const allowedTags = new Set([
  'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'figure', 'table', 'thead',
  'tbody', 'tfoot', 'tr', 'th', 'td',
])
const aboutMeHtml = computed(() => {
  const content = String(props.web_site.text?.about_me ?? '')

  if (typeof DOMParser === 'undefined') {
    return content.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')
  }

  const parsed = new DOMParser().parseFromString(content, 'text/html')
  const sanitizeNode = (node) => {
    if (node.nodeType === 3) {
      return parsed.createTextNode(node.textContent ?? '')
    }

    const fragment = parsed.createDocumentFragment()

    if (node.nodeType !== 1 || ['script', 'style', 'iframe', 'object'].includes(node.tagName.toLowerCase())) {
      return fragment
    }

    const tagName = node.tagName.toLowerCase()
    const container = allowedTags.has(tagName)
      ? parsed.createElement(tagName)
      : fragment

    if (tagName === 'a' && container instanceof Element) {
      const href = node.getAttribute('href')?.trim() ?? ''
      if (/^(https?:\/\/|mailto:|tel:|\/|#|\.\.?\/)/i.test(href) && !href.startsWith('//')) {
        container.setAttribute('href', href)
      }
    }

    if (container instanceof Element && node.hasAttribute('class')) {
      const textAlignment = node.className.match(/\btext-align-(left|center|right|justify)\b/)
      if (textAlignment && ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'th', 'td'].includes(tagName)) {
        container.classList.add(textAlignment[0])
      }
      if (tagName === 'figure' && node.classList.contains('table')) {
        container.classList.add('table')
      }
    }

    Array.from(node.childNodes).forEach((child) => container.appendChild(sanitizeNode(child)))

    return container
  }
  const sanitizedContent = parsed.createElement('div')

  Array.from(parsed.body.childNodes).forEach((node) => {
    sanitizedContent.appendChild(sanitizeNode(node))
  })

  return sanitizedContent.innerHTML
})

onMounted(() => {
  if (aboutText.value && aboutText.value.scrollHeight > 300) {
    shouldShowToggle.value = true
  }
})

const color = ref(props.web_site.color)
const pillars = computed(() => {
    return props.web_site?.pillars?.filter(pillar => pillar.title) || [];
});

// Dynamically compute the grid class
const gridClass = computed(() => {
    const count = pillars.value.length;
    if (count === 1) return 'grid-cols-1 lg:grid-cols-1';
    if (count === 2) return 'grid-cols-1 sm:grid-cols-2';
    if (count === 3) return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
    if (count === 5) return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5';
    return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';
});

</script>
<template>
    <div v-if="web_site.text?.about_me || pillars.length" class="bg-white px-5 py-12 md:py-16">
      <div class="container mx-auto flex flex-col gap-16 md:gap-24">
        <section v-if="web_site.text?.about_me" id="sobre-mim" aria-labelledby="about-me-heading" class="grid items-center gap-6 md:grid-cols-[0.9fr_1.1fr] md:gap-6">
          <div v-if="web_site.image?.profile_picture_path_url" class="relative mx-auto w-full max-w-sm md:max-w-[480px]">
            <div class="absolute -bottom-4 -left-4 h-full w-full rounded-[2rem] xl:-bottom-10 xl:-left-10" :style="{ backgroundColor: color }"></div>
            <img
              :src="web_site.image.profile_picture_path_url"
              :alt="web_site.text?.name || $t('about_me_upper')"
              class="relative aspect-square w-full rounded-[2rem] object-cover object-center"
            />
          </div>
          <div class="min-w-0" :class="{ 'md:col-span-2 md:mx-auto md:max-w-3xl': !web_site.image?.profile_picture_path_url }">
            <h2 id="about-me-heading" class="text-center text-2xl font-bold text-[#3f444a] md:text-left md:text-3xl">
              {{ $t('about_me_upper') }}
            </h2>
            <div class="mb-3 mt-2 h-1 w-8 rounded-full" :style="{ backgroundColor: color }"></div>
            <p v-if="web_site.text?.acronym_and_professional_council" class="mb-3 text-sm text-gray-400">
              {{ web_site.text.acronym_and_professional_council }}
            </p>
            <div class="w-full">
              <div
                ref="aboutText"
                class="text-left font-light leading-relaxed text-[#5c6873] transition-all duration-300 ease-in-out"
                :class="{ 'max-h-[280px] overflow-hidden': !showFullText }"
              >
                <div class="ck-content" v-html="aboutMeHtml"></div>
              </div>

              <div class="text-right mt-2">
                <button
                  v-if="shouldShowToggle"
                  @click="showFullText = !showFullText"
                  :style="{ color: color }"
                  class="font-semibold text-sm hover:underline"
                >
                  {{ showFullText ? $t('see_less') : $t('see_more') }}
                </button>
              </div>
            </div>
          </div>
        </section>

        <section
          v-if="pillars?.length > 0"
          id="pilares-de-atuacao"
          aria-labelledby="practice-pillars-heading"
          class="rounded-3xl px-5 py-8 md:px-10 md:py-10"
          :style="{ backgroundColor: `color-mix(in srgb, ${color} 30%, white)` }"
        >
          <div class="mb-6 text-center">
            <h2 id="practice-pillars-heading" class="text-xl font-bold text-[#3f444a] md:text-2xl">{{ $t('practice_pillars') }}</h2>
            <p class="mt-1 text-sm text-[#5c6873]">{{ $t('practice_pillars_subtitle') }}</p>
            <div class="mx-auto mt-3 h-1 w-8 rounded-full" :style="{ backgroundColor: color }"></div>
          </div>
          <div :class="`grid ${gridClass} items-stretch gap-4 md:gap-6`">
            <article
              v-for="pillar in pillars"
              :key="pillar.id"
              class="flex flex-col items-center rounded-2xl bg-white px-5 py-6 text-center shadow-sm"
            >
              <span class="mb-4 flex size-12 items-center justify-center rounded-full" :style="{ backgroundColor: `color-mix(in srgb, ${color} 30%, white)` }">
                <i class="text-xl" :class="pillar.icon" :style="{ color }"></i>
              </span>
              <h3 class="font-semibold text-[#3f444a]">{{ pillar.title }}</h3>
              <p class="mt-2 text-sm font-light leading-relaxed text-[#5c6873]">{{ pillar.description }}</p>
            </article>
          </div>
        </section>
      </div>
    </div>
  </template>
